import React from 'react';
import { CheckCircle, FileText, BarChart3, Target, Sparkles, Award } from 'lucide-react';

export const Portfolio = () => {
  const capabilities = [
    {
      icon: BarChart3,
      title: "Monitoring Excellence",
      description: "Design and implement comprehensive monitoring frameworks that drive measurable results across diverse African contexts.",
      examples: ["KPI Development", "Real-time Dashboards", "Progress Tracking", "Performance Analytics"],
      gradient: "gradient-primary"
    },
    {
      icon: Target,
      title: "Impact Evaluation",
      description: "Rigorous evaluation methodologies that demonstrate program effectiveness and sustainable development outcomes.",
      examples: ["Baseline Studies", "Impact Assessments", "Cost-Benefit Analysis", "Evidence Generation"],
      gradient: "gradient-warm"
    },
    {
      icon: FileText,
      title: "Strategic Planning",
      description: "Comprehensive strategic frameworks that align with organizational goals and maximize development impact.",
      examples: ["Theory of Change", "Logical Frameworks", "Risk Assessment", "Stakeholder Mapping"],
      gradient: "gradient-nature"
    }
  ];



  return (
    <section id="portfolio" className="section-padding bg-gradient-to-br from-gray-50/30 via-white to-teal-50/20 relative overflow-hidden">
      
      {/* African background elements */}
      <div className="absolute inset-0 pattern-african opacity-15"></div>
      <div className="absolute top-1/4 left-1/3 w-96 h-96 gradient-warm rounded-full opacity-5 blur-3xl animate-bounce-subtle"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 gradient-nature rounded-full opacity-5 blur-3xl animate-bounce-subtle" style={{animationDelay: '2s'}}></div>
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        
        {/* Header with African inspiration */}
        <div className="text-center max-w-5xl mx-auto mb-20 animate-fadeInUp">
          <div className="inline-flex items-center px-8 py-4 bg-white/80 backdrop-blur-sm rounded-full shadow-lg mb-8 group hover:shadow-xl transition-all duration-300">
            <Award className="w-5 h-5 mr-3 text-orange-600 group-hover:rotate-12 transition-transform" />
            <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Excellence in Action</span>
            <Sparkles className="w-4 h-4 ml-3 text-teal-600 animate-pulse" />
          </div>
          
          <h2 className="text-section text-gray-900 mb-8 leading-tight">
            What We <span className="text-gradient-primary">Deliver</span> for Africa
          </h2>
          
          <p className="text-xl-responsive text-gray-600 leading-relaxed">
            Discover our comprehensive M&E capabilities designed to create lasting impact 
            and drive sustainable development outcomes across the African continent.
          </p>
        </div>

        {/* Capabilities with African energy */}
        <div className="grid lg:grid-cols-3 gap-12 mb-24">
          {capabilities.map((capability, index) => {
            const Icon = capability.icon;
            
            return (
              <div key={index} className={`animate-slideInLeft`} style={{animationDelay: `${index * 0.2}s`}}>
                <div className="card-modern h-full group hover:scale-105 transition-all duration-500">
                  
                  {/* Icon with African gradient */}
                  <div className="relative mb-8">
                    <div className={`w-18 h-18 ${capability.gradient} rounded-3xl flex items-center justify-center shadow-xl group-hover:shadow-2xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                      <Icon className="w-9 h-9 text-white" />
                    </div>
                    <div className={`absolute -inset-3 ${capability.gradient} rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-300`}></div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 group-hover:text-gradient-primary transition-all duration-300">
                    {capability.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed mb-8 font-medium text-lg">
                    {capability.description}
                  </p>

                  <div className="space-y-4 mb-10">
                    <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">Key Deliverables:</h4>
                    <ul className="space-y-3">
                      {capability.examples.map((example, exampleIndex) => (
                        <li key={exampleIndex} className="flex items-center group/item">
                          <CheckCircle className="w-5 h-5 text-teal-500 mr-4 flex-shrink-0 group-hover/item:scale-125 transition-transform duration-300" />
                          <span className="text-gray-700 font-medium group-hover/item:text-gray-900 transition-colors duration-300">{example}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>


      </div>
    </section>
  );
};